import { EmptyState, Progress, SegmentedControl, cn } from '@nexo/ui';
import { ChevronRight, ListTodo, Plus } from 'lucide-react';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { useTasks } from '../../lib/queries';
import { useMe } from '../auth/AuthProvider';
import { channelLabel } from '../channels/ChannelSidebar';
import { TaskGroupList } from './TaskGroupList';
import { TaskRow } from './TaskRow';
import { groupByDue, sortTasks } from './taskUtils';
import { useCreateTask } from './useTaskMutations';

type Tab = 'channel' | 'mine';

/** Right-hand panel next to the chat: this conversation's tasks, or mine by urgency. */
export function TaskPanel() {
  const { t } = useTranslation();
  const { team, channels } = useTeam();
  const { channelId } = useParams();
  const me = useMe();
  const tasks = useTasks(team.id);
  const [tab, setTab] = useState<Tab>('channel');
  const [showDone, setShowDone] = useState(false);
  const channel = channelId ? channels.find((c) => c.id === channelId) : undefined;
  const activeTab: Tab = channel ? tab : 'mine';

  const all = tasks.data ?? [];
  const channelTasks = sortTasks(all.filter((task) => task.channelId === channelId));
  const doneCount = channelTasks.filter((task) => task.status === 'done').length;
  const mine = all.filter(
    (task) => task.status !== 'done' && task.assignees.some((a) => a.id === me.id),
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {channel && (
        <div className="px-4 pt-3">
          <SegmentedControl<Tab>
            label={t('Tareas a mostrar')}
            size="sm"
            value={activeTab}
            onChange={setTab}
            className="w-full [&>button]:flex-1 [&>button]:justify-center"
            options={[
              {
                value: 'channel',
                label: t('Esta conversación'),
                count: channelTasks.length - doneCount,
              },
              { value: 'mine', label: t('Mías'), count: mine.length },
            ]}
          />
        </div>
      )}

      <div className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
        {activeTab === 'channel' && channel ? (
          <>
            {channelTasks.length > 0 && (
              <div className="mx-2 mb-3 rounded-xl border border-border bg-surface-2/50 px-3 py-2.5">
                <div className="mb-1.5 flex items-baseline justify-between text-xs">
                  <span className="font-medium">{t('Progreso')}</span>
                  <span className="text-muted tabular-nums">
                    {t('{{done}} de {{total}} hechas', {
                      done: doneCount,
                      total: channelTasks.length,
                    })}
                  </span>
                </div>
                <Progress
                  value={doneCount}
                  max={channelTasks.length}
                  label={t('Tareas completadas')}
                />
              </div>
            )}
            <QuickAdd
              channelId={channel.id}
              placeholder={t('Añadir tarea en {{name}}', {
                name: channel.kind === 'dm' ? channelLabel(channel) : `#${channel.name}`,
              })}
            />
            {channelTasks.length === 0 ? (
              <EmptyState
                icon={<ListTodo className="size-7" />}
                title={t('Sin tareas en esta conversación')}
                description={t('Pasa el ratón por un mensaje y pulsa «Convertir en tarea».')}
              />
            ) : (
              <>
                {channelTasks
                  .filter((task) => task.status !== 'done')
                  .map((task) => (
                    <TaskRow key={task.id} task={task} showChannel={false} compact />
                  ))}
                {doneCount > 0 && (
                  <button
                    type="button"
                    aria-expanded={showDone}
                    onClick={() => setShowDone((v) => !v)}
                    className="mx-2 mt-3 mb-1 flex items-center gap-1.5 text-xs font-medium text-subtle hover:text-text"
                  >
                    <ChevronRight
                      className={cn('size-3.5 transition-transform', showDone && 'rotate-90')}
                    />
                    {t('Completadas ({{count}})', { count: doneCount })}
                  </button>
                )}
                {showDone &&
                  channelTasks
                    .filter((task) => task.status === 'done')
                    .map((task) => (
                      <TaskRow key={task.id} task={task} showChannel={false} compact />
                    ))}
              </>
            )}
          </>
        ) : (
          <>
            <QuickAdd channelId={null} assignToMe placeholder={t('Añadir una tarea para mí')} />
            {mine.length === 0 ? (
              <EmptyState
                icon={<ListTodo className="size-7" />}
                title={t('Nada pendiente')}
                description={t('No tienes tareas abiertas. ¡Bien hecho!')}
              />
            ) : (
              <TaskGroupList groups={groupByDue(mine)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QuickAdd({
  channelId,
  assignToMe = false,
  placeholder,
}: {
  channelId: string | null;
  assignToMe?: boolean;
  placeholder: string;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const create = useCreateTask();
  const [title, setTitle] = useState('');

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const value = title.trim();
    if (!value) return;
    create.mutate({ title: value, channelId, assigneeIds: assignToMe ? [me.id] : [] });
    setTitle('');
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mx-2 mb-2 flex items-center gap-2.5 rounded-lg px-2 py-2 hover:bg-surface-2 focus-within:bg-surface-2"
    >
      <Plus className="size-4 shrink-0 text-subtle" />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={placeholder}
        aria-label={t('Nueva tarea')}
        maxLength={200}
        className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-subtle focus:outline-none"
      />
    </form>
  );
}
